import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SignatureCompletionData,
  useAgreements,
  useMyDataRequest,
  useOmniAuth,
  useScrollToBottom,
  useUser,
} from "../../hooks";
import { MAX_SELECTABLE_INSTITUTIONS } from "../../constants/institutionSelection";
import { InstitutionWithDatasets } from "../../services/index";
import { OmniAuthModal } from "../modals";
import { BottomActionButton } from "../ui";
import Icon from "../ui/Icon";

interface MyDataAgreementPageProps {
  title: string;
  onBack?: () => void;
  successMessage: string;
  buttonText: string;
  autoAgreeButtonText: string;
  selectedInstitutions?: InstitutionWithDatasets[]; // 수정 모드에서 직접 전달
  onBeforeSignature?: () => Promise<boolean>; // 전자서명 시작 전 콜백 (false 반환 시 중단)
  onSignatureComplete?: (data: SignatureCompletionData) => void | Promise<void>; // 전자서명 완료 시 콜백
  successNavigateTo?: string; // 성공 시 이동할 경로 (기본값: "/")
}

const MyDataAgreementPage: React.FC<MyDataAgreementPageProps> = ({
  onBack,
  successMessage,
  buttonText,
  autoAgreeButtonText,
  selectedInstitutions: propsSelectedInstitutions,
  onBeforeSignature,
  onSignatureComplete,
  successNavigateTo = "/",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userName } = useUser();
  const [isProcessing, setIsProcessing] = useState(false);

  // 공통 훅들 사용
  const { mainRef, hasReachedBottom, handleScrollDown } = useScrollToBottom();
  const {
    agreements,
    handleAgreementToggle,
    allAgreementsChecked,
    handleAgreeAll,
    setAllAgreements,
  } = useAgreements();
  const { periodicTransfer, setPeriodicTransfer, endDate, setEndDate } =
    useMyDataRequest();

  // 선택된 기관 정보: props로 받거나 location.state에서 가져오기
  const selectedInstitutions = useMemo<InstitutionWithDatasets[]>(
    () =>
      propsSelectedInstitutions || location.state?.selectedInstitutions || [],
    [propsSelectedInstitutions, location.state?.selectedInstitutions]
  );

  const institutionNameList = useMemo(() => {
    const publicInstitutionNames = selectedInstitutions
      .filter((inst: InstitutionWithDatasets) => inst.type === "public")
      .map((inst: InstitutionWithDatasets) => inst.provideInstitutionName);

    const hospitalNames = selectedInstitutions
      .filter((inst: InstitutionWithDatasets) => inst.type === "hospital")
      .map((inst: InstitutionWithDatasets) => inst.provideInstitutionName);

    const result = [...publicInstitutionNames];

    // 병원이 하나라도 있으면 특정 형식으로 표시
    if (hospitalNames.length > 0) {
      result.push(
        `「의료법」제3조의4에따른 상급종합병원 (${hospitalNames.join(", ")})`
      );
    }

    return result.join(", ");
  }, [selectedInstitutions]);

  const institutionDataSetNameList = useMemo(() => {
    // 기관별 고정 텍스트 매핑
    const publicInstitutionTexts: Record<string, string> = {
      "100002":
        "국민건강보험공단: 진료내용 정보(대상자 현황, 병의원/약국 명칭, 진료개시일, 진료형태, 병원입원일수, 처방횟수, 본인부담금, 병의원 소재지, 정보 식별자), 건강검진 정보(건강검진정보, 영유아건강검진정보, 암검진정보, 정보 식별자)",
      "100004":
        "건강보험심사평가원: 투약이력 정보(조제일자, 약품코드, 약품명(제품명), 성분코드, 함량, 투약량, 투여횟수, 총투약일수, 정보 식별자)",
      "100006":
        "질병관리청: 예방접종 정보(접종자 현황(성명, 성별, 생년월일), 접종일자, 접종기관명, 접종차수, 예방접종명, 정보 식별자)",
    };

    const hospitalText =
      "「의료법」제3조의4에따른 상급종합병원: 환자정보(성명, 성별, 생년월일), 내원정보(내원 상태, 진료 구분, 내원 기간), 진단정보(진단 입원적 상태, 진단명, 진단 및 주호소 정보, 진단 일자), 약품처방정보(약품 처방 상태, 약품 처방 의도, 지침약 여부, 약품 제품명, 약품 처방 일시, 약품 투여 방법),수술 및 처치내역(수술 및 처치 상태, 수술 및 처치명, 수술 및 처치 일자), 진단/기타검사(진단검사 상태, 검사구분(진단, 기타), 검사 항목명, 검사 일시, 검사 결과, 참고구간), 영상/병리검사(검사 상태, 검사구분(영상, 병리), 검사명, 검사 의뢰일, 검사 결과), 알레르기 및 부작용(알레르기 및 부작용 원인 구분, 알레르기 및 부작용 물질, 알레르기 및 부작용 기록 일시, 알레르기 및 부작용 반응, 추가정보), 정보 식별자";

    const result: string[] = [];
    let hasHospital = false;

    selectedInstitutions.forEach((institution: InstitutionWithDatasets) => {
      if (institution.type === "public") {
        // 공공기관: 기관 코드로 고정 텍스트 조회
        const fixedText =
          publicInstitutionTexts[institution.provideInstitutionNo];
        if (fixedText) {
          result.push(fixedText);
        }
      } else if (institution.type === "hospital") {
        // 의료기관: 하나라도 있으면 플래그 설정
        hasHospital = true;
      }
    });

    // 의료기관이 1개 이상 있으면 고정 텍스트 추가
    if (hasHospital) {
      result.push(hospitalText);
    }

    return result;
  }, [selectedInstitutions]);

  // 동의서 텍스트 내용 구성
  const agreementText = useMemo(() => {
    return `
내 의료정보를 불러오기 위한 전송요구서를 확인해주세요

전송요구 목적: 전송요구를 통한 의료마이데이터 기록 조회 및 건강관리 서비스 제공

보내는 곳: ${institutionNameList}

받는 곳: (주)메라키플레이스

전송을 요구하는 개인정보:
${institutionDataSetNameList.join("\n")}

정기적 전송 요구 여부 및 주기: ${
      periodicTransfer === "ONCE_A_WEEK"
        ? "주 1회 업데이트"
        : "업데이트하지 않음"
    }

전송 요구의 종료 시점: ${endDate === "ONE_YEAR" ? "1년" : "6개월"}

보유 및 이용기간: 회원탈퇴 시 또는 전송요구 철회 시까지

전송을 요구하는 정보에 민감정보가 포함되어 있으며, 「개인정보 보호법」제35조의2제2항에 따라 개인정보 전송을 위해 요구합니다.
    `.trim();
  }, [
    institutionNameList,
    institutionDataSetNameList,
    periodicTransfer,
    endDate,
  ]);

  // 간편인증 훅
  const {
    isModalOpen: isOmniAuthModalOpen,
    isLoading: isOmniAuthLoading,
    institutions,
    openAuth,
    closeAuth,
    handleAllSignaturesComplete,
  } = useOmniAuth({
    onSuccess: async (data) => {
      try {
        // 각 페이지에서 전달한 콜백으로 API 호출 처리
        if (onSignatureComplete) {
          await onSignatureComplete(data);
        }
        setIsProcessing(false);
        toast.success(successMessage);
        navigate(successNavigateTo);
      } catch (error) {
        console.error("API 호출 실패:", error);
        setIsProcessing(false);
        throw error;
      }
    },
    onError: (error) => {
      console.error("MyDataAgreementPage - 전자서명 실패:", error);
      setIsProcessing(false);
      toast.error(`연결 실패: ${error}`);
    },
    options: {
      selectedInstitutions,
      periodicTransfer,
      endDate,
      agreementText,
      userName,
    },
  });

  // 전자서명 모달 닫기 (처리 상태 리셋)
  const handleCloseOmniAuth = () => {
    setIsProcessing(false);
    closeAuth();
  };

  const handleComplete = async () => {
    // 모든 항목이 체크되지 않은 경우 자동으로 모두 체크
    if (!allAgreementsChecked) {
      setAllAgreements(true);
    }

    // 전자서명 시작 전 콜백 호출 (예: 데이터 삭제 여부 확인 모달)
    if (onBeforeSignature) {
      const shouldProceed = await onBeforeSignature();
      if (!shouldProceed) {
        return; // 사용자가 취소한 경우 중단
      }
    }

    // 처리 중 상태로 변경
    setIsProcessing(true);

    // 전자서명 모달 열기
    openAuth();
  };

  if (selectedInstitutions.length > MAX_SELECTABLE_INSTITUTIONS) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-4">
        <p className="text-gray-700 text-center mb-4 max-w-md">
          전자서명은 한 번에 최대 {MAX_SELECTABLE_INSTITUTIONS}개 기관까지 연결할 수
          있습니다. 선택된 기관 수가 제한을 초과했습니다.
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-white flex flex-col relative">
      {/* 로딩 오버레이 */}
      {isOmniAuthLoading && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-gray-700 font-medium">전송 처리 중입니다...</p>
              <p className="text-sm text-gray-500 text-center">
                잠시만 기다려주세요
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 상단 헤더 - 고정 */}
      <header className="bg-white px-4 py-3 flex items-center flex-shrink-0">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isOmniAuthLoading}
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>
        )}      </header>

      {/* 메인 콘텐츠 - 스크롤 가능 영역 */}
      <main
        ref={mainRef}
        className="flex-1 p-4 overflow-y-auto min-h-0 pb-4 md:pb-6"
      >
        {/* 전체 내용 */}
        <div className="space-y-4">
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              내 의료정보를 불러오기 위한 전송요구서를 확인해주세요
            </h1>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  전송요구 목적
                </h3>
                <p className="text-sm text-gray-600">
                  전송요구를 통한 의료마이데이터 기록 조회 및 건강관리 서비스
                  제공
                </p>
              </div>
            </div>
            {/* 내 의료정보를 취급하는 곳 */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">보내는 곳</h3>
                <p className="text-sm text-gray-600">{institutionNameList}</p>
              </div>
            </div>

            {/* 내 의료정보를 취급하는 곳 */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">받는 곳</h3>
                <p className="text-sm text-gray-600">(주)메라키플레이스</p>
              </div>
            </div>

            {/* 전송을 요구하는 내 의료정보 */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                전송을 요구하는 개인정보
              </h3>
              <div className="text-sm text-gray-600 space-y-2">
                {institutionDataSetNameList.map((text, index) => {
                  const colonIndex = text.indexOf(":");
                  if (colonIndex === -1) {
                    return <div key={index}>{text}</div>;
                  }
                  const label = text.substring(0, colonIndex + 1);
                  const content = text.substring(colonIndex + 1);
                  return (
                    <div key={index}>
                      {label}
                      <span className="font-bold">{content}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 정기적 전송을 요구하는 여부 및 주기 */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                정기적 전송 요구 여부 및 주기
              </h3>
              <select
                value={periodicTransfer}
                onChange={(e) => setPeriodicTransfer(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="ONCE_A_WEEK">주 1회 업데이트</option>
                <option value="NONE">업데이트하지 않음</option>
              </select>
            </div>

            {/* 전송 요구의 종료 시점 */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                전송 요구의 종료 시점
              </h3>
              <select
                value={endDate}
                onChange={(e) =>
                  setEndDate(e.target.value as "ONE_YEAR" | "SIX_MONTH")
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="ONE_YEAR">1년</option>
                <option value="SIX_MONTH">6개월</option>
              </select>
            </div>

            {/* 보유 및 이용기간 */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                보유 및 이용기간
              </h3>
              <p className="text-sm text-gray-900 font-bold">
                회원탈퇴 시 또는 전송요구 철회 시까지
              </p>
            </div>

            {/* 민감정보 수집 및 이용 동의 */}
            <div>
              <p className="text-sm text-gray-600">
                전송을 요구하는 정보에 민감정보가 포함되어 있으며, 「개인정보
                보호법」제35조의2제2항에 따라 개인정보 전송을 위해 요구합니다.
              </p>
            </div>
          </div>

        {/* 모두 동의하기 버튼 */}
        <div className="mb-4">
          <button
            onClick={handleAgreeAll}
            className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors border flex items-center gap-2 ${
              allAgreementsChecked
                ? "border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100"
                : "border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                allAgreementsChecked
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-400 bg-transparent"
              }`}
            >
              {allAgreementsChecked && (
                <Icon name="check" className="text-white" size="xs" />
              )}
            </div>
            <span>
              {allAgreementsChecked ? "모든 동의 해제하기" : "모두 동의하기"}
            </span>
          </button>
        </div>

        {/* 체크박스 리스트 */}
        <div className="mb-6 space-y-3 pb-20 md:pb-4">
          <div className="flex items-start space-x-3">
            <button
              onClick={() => handleAgreementToggle("healthDataTransfer")}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${
                agreements.healthDataTransfer
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              {agreements.healthDataTransfer && (
                <Icon name="check" className="text-white" size="xs" />
              )}
            </button>
            <span className="text-sm text-gray-700">
              보건의료정보 전송요구 및 개인정보 수집, 이용 동의
            </span>
          </div>
          <div className="flex items-start space-x-3">
            <button
              onClick={() => handleAgreementToggle("personalDataProvision")}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${
                agreements.personalDataProvision
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              {agreements.personalDataProvision && (
                <Icon name="check" className="text-white" size="xs" />
              )}
            </button>
            <span className="text-sm text-gray-700">
              개인정보 제공 동의 (의료 및 공공기관)
            </span>
          </div>
          <div className="flex items-start space-x-3">
            <button
              onClick={() => handleAgreementToggle("healthInfoProvision")}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${
                agreements.healthInfoProvision
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              {agreements.healthInfoProvision && (
                <Icon name="check" className="text-white" size="xs" />
              )}
            </button>
            <span className="text-sm text-gray-700">
              개인정보 제공 동의 (한국보건의료정보원)
            </span>
          </div>
        </div>
      </main>

      {/* 하단 버튼 영역 - 조건부 렌더링 */}
      {!hasReachedBottom ? (
        <BottomActionButton
          onClick={handleScrollDown}
          className="flex items-center justify-center gap-2"
        >
          <span>아래로 스크롤해주세요</span>
          <span className="text-xl">↓</span>
        </BottomActionButton>
      ) : (
        <BottomActionButton
          onClick={handleComplete}
          isLoading={isProcessing || isOmniAuthLoading}
          loadingText="처리중..."
        >
          {!allAgreementsChecked ? autoAgreeButtonText : buttonText}
        </BottomActionButton>
      )}

      {/* 전자서명 모달 */}
      <OmniAuthModal
        isOpen={isOmniAuthModalOpen}
        onClose={handleCloseOmniAuth}
        onAllSignaturesComplete={handleAllSignaturesComplete}
        institutions={institutions}
        text={agreementText}
      />
    </div>
  );
};

export default MyDataAgreementPage;
